import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Download, RefreshCw } from "lucide-react";
import { AdminLogin } from "@/sections/AdminLogin";
import { SubmissionCard } from "@/sections/SubmissionCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PW_KEY = "lh_admin_pw";

const TABS = [
  { id: "quotes", label: "Quotes" },
  { id: "apps", label: "Applications" },
];

function AdminTabs({ tab, onTab, counts }) {
  return (
    <div className="flex gap-2 border-b border-[hsl(var(--lh-bone))]/15 mb-8">
      {TABS.map(({ id, label }) => (
        <button
          key={id}
          onClick={() => onTab(id)}
          className={`px-4 py-3 font-mono text-xs uppercase tracking-[0.16em] border-b-2 transition-colors ${
            tab === id
              ? "border-[hsl(var(--lh-gold))] text-[hsl(var(--lh-gold))]"
              : "border-transparent text-[hsl(var(--lh-bone))]/60 hover:text-[hsl(var(--lh-bone))]"
          }`}
          data-testid={`admin-tab-${id}`}
        >
          {label} ({counts[id] ?? 0})
        </button>
      ))}
    </div>
  );
}

function AdminHeader({ onRefresh, onExport, onLogout }) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-4 mb-10">
      <div>
        <span className="lh-eyebrow">Admin</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Submissions</h1>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={onRefresh} className="lh-btn-ghost" data-testid="admin-refresh"><RefreshCw size={14}/> Refresh</button>
        <button onClick={onExport} className="lh-btn-ghost" data-testid="admin-export"><Download size={14}/> Export CSV</button>
        <button onClick={onLogout} className="lh-btn-ghost" data-testid="admin-logout"><LogOut size={14}/> Sign out</button>
      </div>
    </header>
  );
}

function SubmissionList({ loading, records, type }) {
  if (loading) return <div className="text-[hsl(var(--lh-bone))]/60 py-16 text-center">Loading…</div>;
  if (records.length === 0) return (
    <div className="text-[hsl(var(--lh-bone))]/60 py-16 text-center border border-dashed border-[hsl(var(--lh-bone))]/15" data-testid="admin-empty">
      No submissions yet.
    </div>
  );
  return (
    <div className="space-y-4">
      {records.map((r) => <SubmissionCard key={r.id} record={r} type={type} />)}
    </div>
  );
}

export default function Admin() {
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async (password) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Password": password };
      const [q, a] = await Promise.all([
        axios.get(`${API}/admin/quote-requests`, { headers }),
        axios.get(`${API}/admin/careers-applications`, { headers }),
      ]);
      setQuotes(q.data);
      setApps(a.data);
    } catch {
      toast.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  }, []);

  const verify = useCallback(async (password) => {
    try {
      await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(PW_KEY, password);
      setAuthed(true);
      loadAll(password);
    } catch {
      sessionStorage.removeItem(PW_KEY);
      setAuthed(false);
    }
  }, [loadAll]);

  useEffect(() => {
    const stored = sessionStorage.getItem(PW_KEY);
    if (stored) {
      setPw(stored);
      verify(stored);
    }
  }, [verify]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password: pw });
      sessionStorage.setItem(PW_KEY, pw);
      setAuthed(true);
      loadAll(pw);
      toast.success("Welcome in.");
    } catch {
      toast.error("Invalid password.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(PW_KEY);
    setPw("");
    setAuthed(false);
  };

  const downloadCsv = async () => {
    const which = tab === "quotes" ? "quotes" : "applications";
    try {
      const res = await axios.get(`${API}/admin/export/${which}.csv`, {
        headers: { "X-Admin-Password": pw },
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${which}.csv`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Export failed.");
    }
  };

  if (!authed) {
    return <AdminLogin password={pw} onPasswordChange={setPw} onSubmit={submit} />;
  }

  const records = tab === "quotes" ? quotes : apps;

  return (
    <div className="min-h-screen p-6 md:p-10" data-testid="admin-dashboard">
      <div className="max-w-[1400px] mx-auto">
        <AdminHeader onRefresh={() => loadAll(pw)} onExport={downloadCsv} onLogout={logout} />
        <AdminTabs tab={tab} onTab={setTab} counts={{ quotes: quotes.length, apps: apps.length }} />
        <SubmissionList loading={loading} records={records} type={tab} />
      </div>
    </div>
  );
}
